import * as echarts from 'echarts'

/**
 * 按时间取 rows 的尾部窗口（假设 rows 已按 createTime 升序）
 * @param {Array} rows 盘口变动点
 * @param {number} hours 小时数
 * @returns 距最后一条 3/6 小时内的尾部；不足则全部
 */
export function lastHours (rows, hours) {
  const list = rows || []
  if (!list.length) return []
  const last = new Date(list[list.length - 1].createTime).getTime()
  const cutoff = last - hours * 3600 * 1000
  return list.filter(r => new Date(r.createTime).getTime() >= cutoff)
}

const toNum = (v) => {
  const n = Number(v)
  return isNaN(n) ? null : n
}

/**
 * 亚盘侧：type 传入主(-1)/客(1)时固定该侧；否则按最新盘口符号自动
 * pan<=0 取主队水位(受让方)，pan>0 取客队水位
 */
export function resolvePan (data, type) {
  if (type != null) return type
  const last = data && data[data.length - 1]
  const p = last ? toNum(last.handicap) : null
  return p == null ? 0 : p
}

const fmtLabel = (createTime) => {
  const d = new Date(createTime)
  if (isNaN(d.getTime())) return ''
  return `${d.getMonth() + 1}-${d.getDate()} ${d.getHours()}:${d.getMinutes()}`
}

/**
 * 由变动点构建 K 线序列（保留原页面语义：无影线、开盘=上一条收盘、中位线=两端水位均值）
 * @param {Array} data 时间升序的变动点
 * @param {'asian'|'size'} market
 * @param {number} pan 亚盘侧（<=0 主队 / >0 客队），大小盘忽略
 * @returns {{xData:Array, sData:Array, lineList:Array, metas:Array, lastRow:Object}}
 */
export function buildCandle (data, market, pan) {
  const rows = data || []
  const isAsian = market === 'asian'
  const xData = []
  const sData = []
  const lineList = []
  const metas = []
  const lastRow = rows[rows.length - 1]

  // 中位线：最后一条 主/客(或大/小) 水位的均值
  let deNum = 0
  if (lastRow) {
    const a = toNum(isAsian ? lastRow.host : lastRow.big)
    const b = toNum(isAsian ? lastRow.guest : lastRow.small)
    const sum = (a == null ? 0 : a) + (b == null ? 0 : b)
    deNum = Math.ceil((sum / 2) * 100) / 100
  }

  let lastNum = null
  rows.forEach(it => {
    const num = isAsian
      ? (pan <= 0 ? toNum(it.host) : toNum(it.guest))
      : toNum(it.big)
    if (num == null) return
    const meta = isAsian
      ? [it.host, it.handicap, it.guest]
      : [it.big, it.dw_HANDICAP, it.small]
    xData.push(fmtLabel(it.createTime))
    // candlestick 标准 4 元组 [open, close, lowest, highest]；元信息用平行数组保留
    sData.push(lastNum == null ? [num, num, num, num] : [lastNum, num, num, num])
    metas.push(meta)
    lastNum = num
    lineList.push([deNum, deNum, deNum, deNum])
  })

  return { xData, sData, lineList, metas, lastRow }
}

const DARK = {
  axisLabel: '#94a3b8',
  splitLine: 'rgba(42,58,85,0.5)',
  legend: '#cbd5e1',
  tooltipBg: '#1a2234',
  tooltipBorder: '#2a3a55',
  tooltipText: '#e2e8f0'
}

/**
 * K 线 echarts option
 * @param {'asian'|'size'} market
 * @param {boolean} dark 深色主题（全屏横屏层用）
 */
export function klineOption (xData, sData, lineList, metas, market, dark) {
  const stickColor = ['#4CAF50', '#FF3344']
  const tooltipText = (i, m) => {
    if (market === 'asian') {
      return `主：${m[i][0] == null ? '-' : m[i][0]} <br>盘：${m[i][1] == null ? '-' : m[i][1]}<br>客：${m[i][2] == null ? '-' : m[i][2]}`
    }
    return `大：${m[i][0] == null ? '-' : m[i][0]} <br>盘：${m[i][1] == null ? '-' : m[i][1]}<br>小：${m[i][2] == null ? '-' : m[i][2]}`
  }
  const tip = dark ? {
    backgroundColor: DARK.tooltipBg,
    borderColor: DARK.tooltipBorder,
    borderWidth: 1,
    textStyle: { color: DARK.tooltipText, fontSize: 12 }
  } : { textStyle: { fontSize: 12 } }

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { animation: false },
      padding: 6,
      ...tip,
      formatter: (params) => {
        const idx = params[0].dataIndex
        const time = params[0].name
        const kd = params[0].data
        const rate = (kd[2] - kd[1]) / kd[1] * 100
        const r = rate > 0 ? ('+' + rate.toFixed(2)) : rate.toFixed(2)
        const body = metas && metas[idx] ? tooltipText(idx, metas) : ''
        return `${time}<br>${body} <br><span>涨跌：${r}%</span>`
      }
    },
    grid: { left: '8%', right: '4%', bottom: '10%', top: '14%' },
    legend: {
      icon: 'roundRect',
      orient: 'horizontal',
      top: 6,
      right: 12,
      itemWidth: 24,
      itemHeight: 8,
      itemGap: 18,
      textStyle: dark ? { color: DARK.legend, fontSize: 11 } : undefined
    },
    xAxis: [{
      type: 'category',
      data: xData,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: dark ? { color: DARK.axisLabel, fontSize: 10, hideOverlap: true } : undefined,
      boundaryGap: true,
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [
            { offset: 0, color: 'rgba(18,155,249,0)' }, { offset: 1, color: 'rgba(18,155,249,0.35)' }
          ] }
        }
      }
    }],
    yAxis: [{
      type: 'value',
      scale: true,
      axisLabel: dark ? { color: DARK.axisLabel, fontSize: 10 } : { show: true },
      splitLine: dark ? { show: true, lineStyle: { color: DARK.splitLine, type: 'dashed' } } : { show: true },
      axisTick: { show: false },
      axisLine: { show: false }
    }],
    dataZoom: [{ type: 'inside' }],
    series: [
      {
        name: '水位',
        type: 'candlestick',
        data: sData,
        itemStyle: {
          normal: {
            color: stickColor[1],
            color0: stickColor[0],
            borderColor: stickColor[1],
            borderColor0: stickColor[0]
          }
        }
      },
      {
        name: '中位线',
        type: 'candlestick',
        data: lineList,
        itemStyle: {
          color: '#7aa4fa',
          color0: '#7aa4fa',
          borderColor: '#7aa4fa',
          borderColor0: '#7aa4fa'
        }
      }
    ]
  }
}

/**
 * 渲染 K 线到容器（先 dispose 避免重复实例），返回实例供 resize
 */
export function renderKline (el, xData, sData, lineList, metas, market, dark) {
  if (!el) return null
  const old = echarts.getInstanceByDom(el)
  if (old) old.dispose()
  const chart = echarts.init(el)
  chart.setOption(klineOption(xData, sData, lineList, metas, market, dark))
  return chart
}
